import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Input,
  Progress,
  Text,
  VStack,
  HStack,
  Badge,
  useColorModeValue,
  useDisclosure,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Spinner,
} from '@chakra-ui/react';
import { DragHandleIcon } from '@chakra-ui/icons';
import { useDispatch, useSelector } from 'react-redux';

import {
  getSkuCatalogSyncStatusApi,
  syncSkuCatalogFromListingsApi,
  getSkuManagementSettingsApi,
  updateSkuManagementSettingsApi,
  startEbayItemDetailsEnrichmentApi,
  getEbayEnrichmentStatusApi,
  startEbaySkuDataSyncApi,
  getEbaySkuDataSyncStatusApi,
  resetAllSkuManagementDataApi,
  SKU_MANAGEMENT_RESET_CONFIRMATION_PHRASE,
  getSkuItemsOverviewApi,
  getDataProfileStatsApi,
  startDataProfileRecalcApi,
  resetEbaySkuDataSyncMarkersApi,
} from 'features/sku-management/sku-management.api';
import { getStoresListApi } from 'features/stores/stores.api';
import { clearSkuManagementError } from 'features/sku-management/sku-management.slice';
import skuCatalogSyncSocket from '../../../app/socketSkuCatalogSync';
import skuEbayEnrichmentSocket from '../../../app/socketSkuEbayEnrichment';
import skuEbayDataSyncSocket from '../../../app/socketSkuEbayDataSync';
import skuDataProfileSocket from '../../../app/socketSkuDataProfile';

import { useColors } from './colors';

const initialProgress = {
  current: 0,
  total: 0,
  percentage: 0,
  status: 'idle',
  message: '',
};

const mapServerProgressToUi = (lastProgress, isRunning) => {
  if (!lastProgress) {
    return isRunning
      ? { ...initialProgress, status: 'running', message: 'In progress…' }
      : { ...initialProgress };
  }
  return {
    current: lastProgress.current ?? 0,
    total: lastProgress.total ?? 0,
    percentage: lastProgress.percentage ?? 0,
    status: lastProgress.status ?? 'idle',
    message: lastProgress.message ?? '',
  };
};

const isEbayStore = (store) => {
  const name = store?.marketplace?.name || store?.marketplace?.altName;
  return typeof name === 'string' && name.toLowerCase().includes('ebay');
};

/** Priority IDs first (that exist in stores), then remaining eBay stores by name. */
const buildPriorityRows = (priorityIds, ebayStores) => {
  const byId = new Map(ebayStores.map((s) => [s.id, s]));
  const seen = new Set();
  const rows = [];
  for (const id of priorityIds) {
    const s = byId.get(id);
    if (s) {
      rows.push({ id: s.id, name: s.name || s.label || id });
      seen.add(id);
    }
  }
  const rest = ebayStores
    .filter((s) => !seen.has(s.id))
    .sort((a, b) =>
      String(a.name || a.label || '').localeCompare(String(b.name || b.label || ''), undefined, {
        sensitivity: 'base',
      }),
    );
  for (const s of rest) {
    rows.push({ id: s.id, name: s.name || s.label || s.id });
  }
  return rows;
};

const SkuCatalogSyncSettingsModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const { textColor, mutedTextColor } = useColors();
  const progressTrackBg = useColorModeValue('gray.100', 'whiteAlpha.200');
  const rowBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const rowBorder = useColorModeValue('gray.200', 'whiteAlpha.300');
  const rowDraggingBg = useColorModeValue('blue.50', 'whiteAlpha.150');

  const syncStatus = useSelector((state) => state.skuManagement.syncCatalogFromListingsStatus);
  const syncError = useSelector((state) => state.skuManagement.error);
  const catalogSyncStatus = useSelector((state) => state.skuManagement.catalogSyncStatus);

  const storesList = useSelector((state) => state.stores.list);
  const getStoresListStatus = useSelector((state) => state.stores.getStoresListStatus);
  const skuManagementSettings = useSelector((state) => state.skuManagement.skuManagementSettings);
  const settingsError = useSelector((state) => state.skuManagement.settingsError);
  const updateSettingsStatus = useSelector(
    (state) => state.skuManagement.updateSkuManagementSettingsStatus,
  );

  const ebayEnrichmentStatus = useSelector((state) => state.skuManagement.ebayEnrichmentStatus);
  const ebayEnrichmentError = useSelector((state) => state.skuManagement.ebayEnrichmentError);
  const startEbayEnrichmentStatus = useSelector(
    (state) => state.skuManagement.startEbayEnrichmentStatus,
  );

  const ebaySkuDataSyncStatus = useSelector((state) => state.skuManagement.ebaySkuDataSyncStatus);
  const ebaySkuDataSyncError = useSelector((state) => state.skuManagement.ebaySkuDataSyncError);
  const startEbaySkuDataSyncStatus = useSelector(
    (state) => state.skuManagement.startEbaySkuDataSyncStatus,
  );

  const [progress, setProgress] = useState(initialProgress);
  const [enrichmentProgress, setEnrichmentProgress] = useState(initialProgress);
  const [dataSyncProgress, setDataSyncProgress] = useState(initialProgress);
  const [profileProgress, setProfileProgress] = useState(initialProgress);
  const [profileStats, setProfileStats] = useState({
    withProfile: 0,
    withoutProfile: 0,
    total: 0,
    isRunning: false,
  });
  const [enrichmentStats, setEnrichmentStats] = useState({
    pending: 0,
    completed: 0,
    ineligible: 0,
    total: 0,
  });
  const [dataSyncStats, setDataSyncStats] = useState({
    syncedListings: 0,
    pendingListings: 0,
    totalListings: 0,
  });
  const [resettingSyncMarkers, setResettingSyncMarkers] = useState(false);
  const [priorityRows, setPriorityRows] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);
  const dragIndexRef = useRef(null);
  const resetCancelRef = useRef(null);
  const toast = useToast();

  const {
    isOpen: isResetStep1Open,
    onOpen: onResetStep1Open,
    onClose: onResetStep1Close,
  } = useDisclosure();
  const {
    isOpen: isResetStep2Open,
    onOpen: onResetStep2Open,
    onClose: onResetStep2Close,
  } = useDisclosure();

  const [resetPhrase, setResetPhrase] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [resetError, setResetError] = useState(null);

  const prevSyncStatusRef = useRef(syncStatus);
  const prevEnrichmentStartRef = useRef(startEbayEnrichmentStatus);
  const prevDataSyncStartRef = useRef(startEbaySkuDataSyncStatus);

  useEffect(() => {
    if (isOpen) {
      dispatch(clearSkuManagementError());
      dispatch(getSkuCatalogSyncStatusApi());
      dispatch(getSkuManagementSettingsApi());
      dispatch(getStoresListApi());
      dispatch(getEbaySkuDataSyncStatusApi()).then((res) => {
        if (res.meta.requestStatus === 'fulfilled' && res.payload?.stats) {
          setDataSyncStats(res.payload.stats);
        }
      });
      dispatch(getEbayEnrichmentStatusApi()).then((res) => {
        if (res.meta.requestStatus === 'fulfilled' && res.payload?.stats) {
          setEnrichmentStats(res.payload.stats);
        }
      });
      dispatch(getDataProfileStatsApi()).then((res) => {
        if (res.meta.requestStatus === 'fulfilled' && res.payload) {
          setProfileStats(res.payload);
        }
      });
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (!catalogSyncStatus) {
      return;
    }
    setProgress(mapServerProgressToUi(catalogSyncStatus.lastProgress, catalogSyncStatus.isRunning));
  }, [catalogSyncStatus]);

  useEffect(() => {
    if (!ebayEnrichmentStatus) {
      return;
    }
    setEnrichmentProgress(
      mapServerProgressToUi(ebayEnrichmentStatus.lastProgress, ebayEnrichmentStatus.isRunning),
    );
  }, [ebayEnrichmentStatus]);

  useEffect(() => {
    if (!ebaySkuDataSyncStatus) {
      return;
    }
    setDataSyncProgress(
      mapServerProgressToUi(ebaySkuDataSyncStatus.lastProgress, ebaySkuDataSyncStatus.isRunning),
    );
    if (ebaySkuDataSyncStatus.stats) {
      setDataSyncStats(ebaySkuDataSyncStatus.stats);
    }
  }, [ebaySkuDataSyncStatus]);

  useEffect(() => {
    if (!ebayEnrichmentStatus?.stats) {
      return;
    }
    setEnrichmentStats(ebayEnrichmentStatus.stats);
  }, [ebayEnrichmentStatus]);

  useEffect(() => {
    if (!Array.isArray(storesList) || !skuManagementSettings) {
      return;
    }
    const ebayOnly = storesList.filter(isEbayStore);
    setPriorityRows(
      buildPriorityRows(skuManagementSettings.ebayStoreFetchPriority || [], ebayOnly),
    );
  }, [storesList, skuManagementSettings]);

  useEffect(() => {
    if (prevSyncStatusRef.current === 'pending' && syncStatus === 'fulfilled') {
      dispatch(getSkuCatalogSyncStatusApi());
    }
    prevSyncStatusRef.current = syncStatus;
  }, [syncStatus, dispatch]);

  useEffect(() => {
    if (prevEnrichmentStartRef.current === 'pending' && startEbayEnrichmentStatus === 'fulfilled') {
      dispatch(getEbayEnrichmentStatusApi());
    }
    prevEnrichmentStartRef.current = startEbayEnrichmentStatus;
  }, [startEbayEnrichmentStatus, dispatch]);

  useEffect(() => {
    if (prevDataSyncStartRef.current === 'pending' && startEbaySkuDataSyncStatus === 'fulfilled') {
      dispatch(getEbaySkuDataSyncStatusApi());
    }
    prevDataSyncStartRef.current = startEbaySkuDataSyncStatus;
  }, [startEbaySkuDataSyncStatus, dispatch]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    skuCatalogSyncSocket.connect();
    skuEbayEnrichmentSocket.connect();
    skuEbayDataSyncSocket.connect();
    skuDataProfileSocket.connect();

    const onCatalogProgress = (data) => {
      setProgress({
        current: data.current ?? 0,
        total: data.total ?? 0,
        percentage: data.percentage ?? 0,
        status: data.status ?? 'running',
        message: data.message ?? '',
      });
    };

    const onCatalogStatus = (data) => {
      if (data?.isRunning) {
        setProgress((prev) => ({
          ...prev,
          status: 'running',
          message: data.message || prev.message || 'Running…',
        }));
      } else if (data?.message) {
        setProgress((prev) => ({
          ...prev,
          message: data.message,
        }));
      }
    };

    const onEnrichmentProgress = (data) => {
      setEnrichmentProgress({
        current: data.current ?? 0,
        total: data.total ?? 0,
        percentage: data.percentage ?? 0,
        status: data.status ?? 'running',
        message: data.message ?? '',
      });
    };

    const onEnrichmentStatus = (data) => {
      if (data?.isRunning) {
        setEnrichmentProgress((prev) => ({
          ...prev,
          status: 'running',
          message: data.message || prev.message || 'Running…',
        }));
      } else if (data?.message) {
        setEnrichmentProgress((prev) => ({
          ...prev,
          message: data.message,
        }));
      }
    };

    skuCatalogSyncSocket.on('sku-catalog-sync-progress', onCatalogProgress);
    skuCatalogSyncSocket.on('sku-catalog-sync-status', onCatalogStatus);
    skuEbayEnrichmentSocket.on('sku-ebay-enrichment-progress', onEnrichmentProgress);
    skuEbayEnrichmentSocket.on('sku-ebay-enrichment-status', onEnrichmentStatus);

    const onDataSyncProgress = (data) => {
      setDataSyncProgress({
        current: data.current ?? 0,
        total: data.total ?? 0,
        percentage: data.percentage ?? 0,
        status: data.status ?? 'running',
        message: data.message ?? '',
      });
    };

    const onDataSyncStatus = (data) => {
      if (data?.isRunning) {
        setDataSyncProgress((prev) => ({
          ...prev,
          status: 'running',
          message: data.message || prev.message || 'Running…',
        }));
      } else if (data?.message) {
        setDataSyncProgress((prev) => ({
          ...prev,
          message: data.message,
        }));
      }
    };

    skuEbayDataSyncSocket.on('sku-ebay-data-sync-progress', onDataSyncProgress);
    skuEbayDataSyncSocket.on('sku-ebay-data-sync-status', onDataSyncStatus);

    const onProfileProgress = (data) => {
      setProfileProgress({
        current: data.current ?? 0,
        total: data.total ?? 0,
        percentage: data.percentage ?? 0,
        status: data.status ?? 'running',
        message: data.message ?? '',
      });
      if (data.withProfile != null || data.withoutProfile != null) {
        setProfileStats((prev) => ({
          ...prev,
          withProfile: data.withProfile ?? prev.withProfile,
          withoutProfile: data.withoutProfile ?? prev.withoutProfile,
          total: (data.withProfile ?? 0) + (data.withoutProfile ?? 0) || prev.total,
        }));
      }
    };

    const onProfileStatus = (data) => {
      setProfileStats((prev) => ({ ...prev, isRunning: !!data?.isRunning }));
      if (!data?.isRunning) {
        dispatch(getDataProfileStatsApi()).then((res) => {
          if (res.meta.requestStatus === 'fulfilled' && res.payload) {
            setProfileStats(res.payload);
          }
        });
      }
    };

    skuDataProfileSocket.on('progress', onProfileProgress);
    skuDataProfileSocket.on('status', onProfileStatus);

    return () => {
      skuCatalogSyncSocket.off('sku-catalog-sync-progress', onCatalogProgress);
      skuCatalogSyncSocket.off('sku-catalog-sync-status', onCatalogStatus);
      skuEbayEnrichmentSocket.off('sku-ebay-enrichment-progress', onEnrichmentProgress);
      skuEbayEnrichmentSocket.off('sku-ebay-enrichment-status', onEnrichmentStatus);
      skuEbayDataSyncSocket.off('sku-ebay-data-sync-progress', onDataSyncProgress);
      skuEbayDataSyncSocket.off('sku-ebay-data-sync-status', onDataSyncStatus);
      skuDataProfileSocket.off('progress', onProfileProgress);
      skuDataProfileSocket.off('status', onProfileStatus);
      skuCatalogSyncSocket.disconnect();
      skuEbayEnrichmentSocket.disconnect();
      skuEbayDataSyncSocket.disconnect();
      skuDataProfileSocket.disconnect();
    };
  }, [isOpen, dispatch]);

  const handleStartDataProfileRecalc = () => {
    setProfileProgress({
      ...initialProgress,
      status: 'running',
      message: 'Starting data profile recalculation…',
    });
    dispatch(startDataProfileRecalcApi());
  };

  const handleStartCatalogSync = () => {
    setProgress({
      ...initialProgress,
      status: 'running',
      message: 'Requesting sync…',
    });
    dispatch(syncSkuCatalogFromListingsApi());
  };

  const handleSavePriority = () => {
    dispatch(
      updateSkuManagementSettingsApi({
        ebayStoreFetchPriority: priorityRows.map((r) => r.id),
      }),
    );
  };

  const reorderRows = useCallback((fromIndex, toIndex) => {
    if (fromIndex === null || fromIndex === toIndex) {
      setDragIndex(null);
      return;
    }
    setPriorityRows((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
    setDragIndex(null);
  }, []);

  const handleStartEnrichment = () => {
    setEnrichmentProgress({
      ...initialProgress,
      status: 'running',
      message: 'Requesting enrichment…',
    });
    dispatch(startEbayItemDetailsEnrichmentApi());
  };

  const handleResetDataSyncMarkers = async () => {
    setResettingSyncMarkers(true);
    try {
      const res = await dispatch(resetEbaySkuDataSyncMarkersApi());
      if (res.meta.requestStatus === 'fulfilled') {
        toast({
          title: 'Sync markers reset',
          description: res.payload?.message || 'All listings will be eligible on the next sync.',
          status: 'success',
          duration: 5000,
        });
        dispatch(getEbaySkuDataSyncStatusApi());
      } else {
        toast({
          title: 'Reset failed',
          description: res.payload?.message || 'Could not reset sync markers',
          status: 'error',
          duration: 5000,
        });
      }
    } finally {
      setResettingSyncMarkers(false);
    }
  };

  const handleStartDataSync = () => {
    setDataSyncProgress({
      ...initialProgress,
      status: 'running',
      message: 'Requesting sync…',
    });
    dispatch(startEbaySkuDataSyncApi());
  };

  const closeResetDialogs = useCallback(() => {
    onResetStep1Close();
    onResetStep2Close();
    setResetPhrase('');
    setResetError(null);
  }, [onResetStep1Close, onResetStep2Close]);

  const handleResetStep1Continue = () => {
    onResetStep1Close();
    setResetPhrase('');
    setResetError(null);
    onResetStep2Open();
  };

  const handleConfirmResetAll = async () => {
    if (resetPhrase.trim() !== SKU_MANAGEMENT_RESET_CONFIRMATION_PHRASE) {
      return;
    }
    setIsResetting(true);
    setResetError(null);
    try {
      const result = await dispatch(
        resetAllSkuManagementDataApi({
          confirmationPhrase: resetPhrase.trim(),
        }),
      ).unwrap();
      closeResetDialogs();
      setProgress(initialProgress);
      setEnrichmentProgress(initialProgress);
      setDataSyncProgress(initialProgress);
      setPriorityRows([]);
      dispatch(getSkuCatalogSyncStatusApi());
      dispatch(getEbayEnrichmentStatusApi());
      dispatch(getEbaySkuDataSyncStatusApi());
      dispatch(getSkuManagementSettingsApi());
      dispatch(getSkuItemsOverviewApi());
      toast({
        title: 'SKU management data reset',
        description: `Removed ${result.deletedSkuDocuments ?? 0} SKU record(s) and ${result.deletedCatalogChunkFiles ?? 0} catalog chunk file(s).`,
        status: 'success',
        duration: 6000,
        isClosable: true,
      });
    } catch (err) {
      const message =
        err?.message || err || 'Could not reset SKU management data.';
      setResetError(message);
      toast({
        title: 'Reset failed',
        description: message,
        status: 'error',
        duration: 8000,
        isClosable: true,
      });
    } finally {
      setIsResetting(false);
    }
  };

  const catalogStatusColor =
    progress.status === 'error'
      ? 'red'
      : progress.status === 'completed'
        ? 'green'
        : progress.status === 'running'
          ? 'blue'
          : 'gray';

  const enrichmentStatusColor =
    enrichmentProgress.status === 'error'
      ? 'red'
      : enrichmentProgress.status === 'completed'
        ? 'green'
        : enrichmentProgress.status === 'running'
          ? 'blue'
          : 'gray';

  const dataSyncStatusColor =
    dataSyncProgress.status === 'error'
      ? 'red'
      : dataSyncProgress.status === 'completed'
        ? 'green'
        : dataSyncProgress.status === 'running'
          ? 'blue'
          : 'gray';

  const storesLoading = getStoresListStatus === 'pending';
  const ebayStoreCount = Array.isArray(storesList) ? storesList.filter(isEbayStore).length : 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="85vh" w="full" maxW="min(80rem, 98vw)">
        <ModalHeader color={textColor}>SKU management</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Tabs variant="enclosed" colorScheme="blue">
            <TabList flexWrap="nowrap" width="100%" overflowX="auto" gap={0} pb={1}>
              <Tab flexShrink={0} whiteSpace="nowrap" fontSize="sm" px={4}>
                Catalog from listings
              </Tab>
              <Tab flexShrink={0} whiteSpace="nowrap" fontSize="sm" px={4}>
                eBay store priority
              </Tab>
              <Tab flexShrink={0} whiteSpace="nowrap" fontSize="sm" px={4}>
                eBay item enrichment
              </Tab>
              <Tab flexShrink={0} whiteSpace="nowrap" fontSize="sm" px={4}>
                eBay SKU data sync
              </Tab>
              <Tab flexShrink={0} whiteSpace="nowrap" fontSize="sm" px={4}>
                Data profile
              </Tab>
              <Tab flexShrink={0} whiteSpace="nowrap" fontSize="sm" px={4} color="red.500">
                Reset data
              </Tab>
            </TabList>
            <TabPanels>
              <TabPanel px={0}>
                <VStack align="stretch" spacing={4}>
                  <Text color={mutedTextColor} fontSize="sm">
                    Scans all marketplace listings, normalizes each SKU (including supplier prefix
                    stripping), then merges into existing SKU documents by canonical key so reruns do
                    not create duplicates. Listing links on each SKU always match the current listing
                    database (deleted listings are unlinked; the same SKU can be relinked when it
                    appears again). Suppliers are merged, titles and catalog images are
                    preserved/merged. Chunk files are rebuilt from the database after each run.
                  </Text>
                  <Text color={mutedTextColor} fontSize="xs">
                    Sync runs on the server. Refreshing the page or closing this dialog does not stop
                    it. Reopen this panel anytime to see the latest progress (saved on the server and
                    replayed over the socket when you connect).
                  </Text>
                  <Button
                    colorScheme="blue"
                    onClick={handleStartCatalogSync}
                    isLoading={syncStatus === 'pending'}
                  >
                    Start sync from listings
                  </Button>
                  {syncError ? (
                    <Text color="red.500" fontSize="sm">
                      {syncError}
                    </Text>
                  ) : null}
                  <VStack align="stretch" spacing={2} width="100%">
                    <HStack justify="space-between" width="100%">
                      <Text fontWeight="medium" color={textColor}>
                        Progress
                      </Text>
                      <Badge colorScheme={catalogStatusColor}>{progress.status}</Badge>
                    </HStack>
                    <Box width="100%">
                      <Progress
                        value={progress.percentage}
                        size="md"
                        width="100%"
                        bg={progressTrackBg}
                        colorScheme="blue"
                        borderRadius="full"
                        hasStripe={progress.status === 'running'}
                        isAnimated={progress.status === 'running'}
                      />
                    </Box>
                    <Text fontSize="xs" color={mutedTextColor}>
                      {progress.message || 'Idle'}
                    </Text>
                    {progress.total > 0 ? (
                      <Text fontSize="xs" color={mutedTextColor}>
                        {progress.current} / {progress.total}
                      </Text>
                    ) : null}
                  </VStack>
                </VStack>
              </TabPanel>

              <TabPanel px={0}>
                <VStack align="stretch" spacing={4}>
                  <Text color={mutedTextColor} fontSize="sm">
                    When a SKU has active eBay listings on more than one store, enrichment uses{' '}
                    <strong>GetItem</strong> on a single listing. Drag stores into priority order
                    (top = highest). Stores not listed here are ordered after your list, by id.
                  </Text>
                  <Text color={mutedTextColor} fontSize="xs">
                    Only eBay-connected stores appear here ({ebayStoreCount} found). Same priority API
                    is used server-side when resolving which listing to fetch.
                  </Text>
                  {storesLoading ? (
                    <HStack>
                      <Spinner size="sm" />
                      <Text fontSize="sm" color={mutedTextColor}>
                        Loading stores…
                      </Text>
                    </HStack>
                  ) : null}
                  {settingsError ? (
                    <Text color="red.500" fontSize="sm">
                      {settingsError}
                    </Text>
                  ) : null}
                  <VStack align="stretch" spacing={2} maxH="280px" overflowY="auto">
                    {priorityRows.map((row, index) => (
                      <HStack
                        key={row.id}
                        draggable
                        onDragStart={() => {
                          dragIndexRef.current = index;
                          setDragIndex(index);
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.dataTransfer.dropEffect = 'move';
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          reorderRows(dragIndexRef.current, index);
                        }}
                        onDragEnd={() => {
                          dragIndexRef.current = null;
                          setDragIndex(null);
                        }}
                        px={3}
                        py={2}
                        borderRadius="md"
                        borderWidth="1px"
                        borderColor={rowBorder}
                        bg={dragIndex === index ? rowDraggingBg : rowBg}
                        cursor="grab"
                        _active={{ cursor: 'grabbing' }}
                        spacing={3}
                      >
                        <DragHandleIcon color={mutedTextColor} boxSize={4} flexShrink={0} />
                        <Text fontSize="sm" color={textColor} flex={1} noOfLines={1}>
                          {index + 1}. {row.name}
                        </Text>
                      </HStack>
                    ))}
                  </VStack>
                  {priorityRows.length === 0 && !storesLoading ? (
                    <Text fontSize="sm" color={mutedTextColor}>
                      No eBay stores found. Add an eBay marketplace store first.
                    </Text>
                  ) : null}
                  <Button
                    colorScheme="blue"
                    onClick={handleSavePriority}
                    isLoading={updateSettingsStatus === 'pending'}
                    isDisabled={priorityRows.length === 0}
                  >
                    Save priority order
                  </Button>
                </VStack>
              </TabPanel>

              <TabPanel px={0}>
                <VStack align="stretch" spacing={4}>
                  <Text color={mutedTextColor} fontSize="sm">
                    Fetches <strong>item specifics</strong> and <strong>fitment</strong> (compatibility)
                    from eBay Trading API <strong>GetItem</strong> for SKUs created via{' '}
                    <strong>Catalog from listings</strong> that have not been enriched yet, using the store
                    priority above. Data is merged into the SKU catalog chunk JSON on disk.
                  </Text>
                  <Text color={mutedTextColor} fontSize="xs">
                    Each eligible SKU is enriched only once. Runs on the server with high concurrency;
                    refresh or close this dialog and reopen to reconnect to live progress.
                  </Text>
                  <HStack spacing={4} flexWrap="wrap">
                    <Badge colorScheme="gray" variant="subtle">
                      Pending: {enrichmentStats.pending ?? 0}
                    </Badge>
                    <Badge colorScheme="green" variant="subtle">
                      Enriched: {enrichmentStats.completed ?? 0}
                    </Badge>
                    <Badge colorScheme="orange" variant="subtle">
                      Other sources: {enrichmentStats.ineligible ?? 0}
                    </Badge>
                  </HStack>
                  <Button
                    colorScheme="blue"
                    onClick={handleStartEnrichment}
                    isLoading={startEbayEnrichmentStatus === 'pending'}
                  >
                    Start eBay item enrichment
                  </Button>
                  {ebayEnrichmentError ? (
                    <Text color="red.500" fontSize="sm">
                      {ebayEnrichmentError}
                    </Text>
                  ) : null}
                  <VStack align="stretch" spacing={2} width="100%">
                    <HStack justify="space-between" width="100%">
                      <Text fontWeight="medium" color={textColor}>
                        Progress
                      </Text>
                      <Badge colorScheme={enrichmentStatusColor}>{enrichmentProgress.status}</Badge>
                    </HStack>
                    <Box width="100%">
                      <Progress
                        value={enrichmentProgress.percentage}
                        size="md"
                        width="100%"
                        bg={progressTrackBg}
                        colorScheme="blue"
                        borderRadius="full"
                        hasStripe={enrichmentProgress.status === 'running'}
                        isAnimated={enrichmentProgress.status === 'running'}
                      />
                    </Box>
                    <Text fontSize="xs" color={mutedTextColor}>
                      {enrichmentProgress.message || 'Idle'}
                    </Text>
                    {enrichmentProgress.total > 0 ? (
                      <Text fontSize="xs" color={mutedTextColor}>
                        {enrichmentProgress.current} / {enrichmentProgress.total}
                      </Text>
                    ) : null}
                  </VStack>
                </VStack>
              </TabPanel>

              <TabPanel px={0}>
                <VStack align="stretch" spacing={4}>
                  <Text color={mutedTextColor} fontSize="sm">
                    Pushes saved catalog data (title, images, item specifics, fitment) to every linked
                    eBay listing for SKUs that have catalog chunks — the same operation as{' '}
                    <strong>Resync data</strong> on each row, run in bulk with high concurrency.
                  </Text>
                  <Text color={mutedTextColor} fontSize="xs">
                    Each listing is retried up to two times on API failure; per-listing status is stored on
                    the SKU and shown in the store coverage table. Reruns only process listings not yet marked
                    successful. Runs on the server; reopen this dialog to reconnect to progress.
                  </Text>
                  <HStack spacing={4} flexWrap="wrap">
                    <Badge colorScheme="green" variant="subtle">
                      Synced listings: {dataSyncStats.syncedListings ?? 0}
                    </Badge>
                    <Badge colorScheme="gray" variant="subtle">
                      Pending: {dataSyncStats.pendingListings ?? 0}
                    </Badge>
                    <Badge colorScheme="blue" variant="subtle">
                      Total links: {dataSyncStats.totalListings ?? 0}
                    </Badge>
                  </HStack>
                  <Button
                    colorScheme="blue"
                    onClick={handleStartDataSync}
                    isLoading={startEbaySkuDataSyncStatus === 'pending'}
                  >
                    Start eBay SKU data sync
                  </Button>
                  <Button
                    variant="outline"
                    colorScheme="orange"
                    onClick={handleResetDataSyncMarkers}
                    isLoading={resettingSyncMarkers}
                  >
                    Reset sync markers (re-sync all listings)
                  </Button>
                  {ebaySkuDataSyncError ? (
                    <Text color="red.500" fontSize="sm">
                      {ebaySkuDataSyncError}
                    </Text>
                  ) : null}
                  <VStack align="stretch" spacing={2} width="100%">
                    <HStack justify="space-between" width="100%">
                      <Text fontWeight="medium" color={textColor}>
                        Progress
                      </Text>
                      <Badge colorScheme={dataSyncStatusColor}>{dataSyncProgress.status}</Badge>
                    </HStack>
                    <Box width="100%">
                      <Progress
                        value={dataSyncProgress.percentage}
                        size="md"
                        width="100%"
                        bg={progressTrackBg}
                        colorScheme="blue"
                        borderRadius="full"
                        hasStripe={dataSyncProgress.status === 'running'}
                        isAnimated={dataSyncProgress.status === 'running'}
                      />
                    </Box>
                    <Text fontSize="xs" color={mutedTextColor}>
                      {dataSyncProgress.message || 'Idle'}
                    </Text>
                    {dataSyncProgress.total > 0 ? (
                      <Text fontSize="xs" color={mutedTextColor}>
                        {dataSyncProgress.current} / {dataSyncProgress.total}
                      </Text>
                    ) : null}
                  </VStack>
                </VStack>
              </TabPanel>

              <TabPanel px={0}>
                <VStack align="stretch" spacing={4}>
                  <Text color={mutedTextColor} fontSize="sm">
                    Calculates fitment, specifications, and image completeness from each SKU catalog
                    chunk and saves the result on the SKU document. Use this after enrichment or when
                    filters show &quot;Not calculated&quot;. Thresholds are configured in{' '}
                    <code>server/src/services/sku-management/config/data-profile-thresholds.json</code>.
                  </Text>
                  <HStack spacing={4} flexWrap="wrap">
                    <Badge colorScheme="green" variant="subtle">
                      Saved: {profileStats.withProfile ?? 0}
                    </Badge>
                    <Badge colorScheme="gray" variant="subtle">
                      Not calculated: {profileStats.withoutProfile ?? 0}
                    </Badge>
                    <Badge colorScheme="blue" variant="subtle">
                      Total SKUs: {profileStats.total ?? 0}
                    </Badge>
                  </HStack>
                  <Button
                    colorScheme="blue"
                    onClick={handleStartDataProfileRecalc}
                    isDisabled={profileStats.isRunning}
                  >
                    Recalculate all data profiles
                  </Button>
                  <VStack align="stretch" spacing={2} width="100%">
                    <HStack justify="space-between" width="100%">
                      <Text fontWeight="medium" color={textColor}>
                        Progress
                      </Text>
                      <Badge
                        colorScheme={
                          profileProgress.status === 'error'
                            ? 'red'
                            : profileProgress.status === 'completed'
                              ? 'green'
                              : profileProgress.status === 'running'
                                ? 'blue'
                                : 'gray'
                        }
                      >
                        {profileProgress.status}
                      </Badge>
                    </HStack>
                    <Progress
                      value={profileProgress.percentage}
                      size="md"
                      width="100%"
                      bg={progressTrackBg}
                      colorScheme="blue"
                      borderRadius="full"
                      hasStripe={profileProgress.status === 'running'}
                      isAnimated={profileProgress.status === 'running'}
                    />
                    <Text fontSize="xs" color={mutedTextColor}>
                      {profileProgress.message || 'Idle'}
                    </Text>
                  </VStack>
                </VStack>
              </TabPanel>

              <TabPanel px={0}>
                <VStack align="stretch" spacing={4}>
                  <Text color={mutedTextColor} fontSize="sm">
                    Permanently deletes all SKU management data: every SKU document in the database,
                    all catalog chunk JSON files on disk, and job progress tracking files. eBay store
                    priority settings are cleared. Marketplace listings in the listing service are not
                    deleted — run <strong>Catalog from listings</strong> again to rebuild SKUs.
                  </Text>
                  <Text color="red.500" fontSize="sm" fontWeight="medium">
                    This cannot be undone. Background sync, enrichment, or data-sync jobs must be
                    stopped before reset.
                  </Text>
                  <Button colorScheme="red" variant="outline" onClick={onResetStep1Open}>
                    Delete all SKU management data…
                  </Button>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>

      <AlertDialog
        isOpen={isResetStep1Open}
        leastDestructiveRef={resetCancelRef}
        onClose={closeResetDialogs}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader color={textColor}>Delete all SKU management data?</AlertDialogHeader>
            <AlertDialogBody color={mutedTextColor}>
              This removes all SKU records, catalog chunk files, and saved sync/enrichment progress.
              Listings in your listing database are not affected.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={resetCancelRef} onClick={closeResetDialogs}>
                Cancel
              </Button>
              <Button colorScheme="red" ml={3} onClick={handleResetStep1Continue}>
                Continue
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <AlertDialog
        isOpen={isResetStep2Open}
        leastDestructiveRef={resetCancelRef}
        onClose={closeResetDialogs}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader color={textColor}>Final confirmation</AlertDialogHeader>
            <AlertDialogBody>
              <VStack align="stretch" spacing={3}>
                <Text color={mutedTextColor} fontSize="sm">
                  Type{' '}
                  <Text as="span" fontFamily="mono" fontWeight="bold" color={textColor}>
                    {SKU_MANAGEMENT_RESET_CONFIRMATION_PHRASE}
                  </Text>{' '}
                  to confirm.
                </Text>
                <Input
                  value={resetPhrase}
                  onChange={(e) => setResetPhrase(e.target.value)}
                  placeholder={SKU_MANAGEMENT_RESET_CONFIRMATION_PHRASE}
                  autoComplete="off"
                  spellCheck={false}
                />
                {resetError ? (
                  <Text color="red.500" fontSize="sm">
                    {resetError}
                  </Text>
                ) : null}
              </VStack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={resetCancelRef} onClick={closeResetDialogs} isDisabled={isResetting}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                ml={3}
                onClick={handleConfirmResetAll}
                isLoading={isResetting}
                isDisabled={resetPhrase.trim() !== SKU_MANAGEMENT_RESET_CONFIRMATION_PHRASE}
              >
                Delete everything
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Modal>
  );
};

export default SkuCatalogSyncSettingsModal;
